import { useIsDemo } from "@/demo/DemoContext";
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ExternalLink, Link2, RefreshCw, SearchX } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCurrency, formatDate } from "@/lib/format";
import { PageContainer, PageHeader, BackLink } from "@/components/PagePrimitives";
import {
  HeaderRow,
  ReceiptPreview,
  TH,
  TableShell,
  ScoreMeter,
  prettyMerchant,
  receiptSourceLabel,
} from "@/components/ExpenseUi";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { components } from "@dolfin/api-public";

type ReceiptResponse = components["schemas"]["ReceiptResponse"];
type MatchCandidateResponse = components["schemas"]["MatchCandidateResponse"];

const CANDIDATES_SHOWN = 3;

// Ordered by what a person must decide: unreadable photo first, then a missing VAT number, then what was read; the file last.
export function ReceiptDetailPage({
  receiptId,
  onBack,
  onExpenseClick,
}: {
  receiptId: string;
  onBack?: () => void;
  onExpenseClick?: (expenseId: string) => void;
}) {
  const isDemo = useIsDemo();
  const api = useDolfinClient();
  const [receipt, setReceipt] = useState<ReceiptResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Only asked for while unmatched and read: before that there is nothing to score against.
  const [candidates, setCandidates] = useState<MatchCandidateResponse[] | null>(null);
  const [matching, setMatching] = useState<string | null>(null);
  const [showAllCandidates, setShowAllCandidates] = useState(false);

  const fetchReceipt = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: apiError } = await api.GET("/v1/receipts/{id}", {
      params: { path: { id: receiptId } },
    });
    if (apiError || !data) {
      setError("Couldn't load this receipt.");
    } else {
      setReceipt(data);
    }
    setLoading(false);
  }, [api, receiptId]);

  useEffect(() => {
    fetchReceipt();
  }, [fetchReceipt]);

  const unmatchedAndRead = receipt?.matchStatus === "Unmatched" && receipt.extractionStatus !== "Pending";
  useEffect(() => {
    if (!unmatchedAndRead) {
      setCandidates(null);
      return;
    }
    let cancelled = false;
    api
      .GET("/v1/receipts/{id}/match-candidates", { params: { path: { id: receiptId } } })
      .then(({ data }) => !cancelled && setCandidates(data ?? []));
    return () => {
      cancelled = true;
    };
  }, [api, receiptId, unmatchedAndRead]);

  async function matchTo(expenseId: string) {
    setMatching(expenseId);
    const { error: apiError } = await api.POST("/v1/receipts/{id}/match", {
      params: { path: { id: receiptId } },
      body: { expenseId },
    });
    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Couldn't match the receipt."));
    } else {
      toast.success("Matched. The expense has been re-coded from this receipt.");
      // Refetched rather than taken from the match response, which does not carry the receipt's lines.
      await fetchReceipt();
    }
    setMatching(null);
  }

  async function retryExtraction() {
    setRetrying(true);
    const { error: apiError } = await api.POST("/v1/receipts/{id}/retry-extraction", {
      params: { path: { id: receiptId } },
    });
    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Couldn't re-read the receipt."));
    } else {
      toast.success("Reading the receipt again.");
      await fetchReceipt();
    }
    setRetrying(false);
  }

  async function openFile() {
    const { data, error: apiError } = await api.GET("/v1/receipts/{id}/file-url", {
      params: { path: { id: receiptId } },
    });
    if (apiError || !data?.url) {
      toast.error(apiErrorMessage(apiError, "Couldn't open the file."));
      return;
    }
    window.open(data.url, "_blank", "noopener,noreferrer");
  }

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center gap-2 p-10 text-sm text-muted-foreground">
          <Spinner className="size-4" /> Loading receipt...
        </div>
      </PageContainer>
    );
  }

  if (error || !receipt) {
    return (
      <PageContainer>
        {onBack && <BackLink onClick={onBack}>Back to receipts</BackLink>}
        <p className="text-destructive text-sm">{error ?? "Receipt not found."}</p>
      </PageContainer>
    );
  }

  const currency = receipt.currency;
  const unreadable = receipt.extractionStatus === "Failed";
  const stillReading = receipt.extractionStatus === "Pending";
  const missingVatNumber = !receipt.merchantVatNumber && !unreadable && !stillReading;

  return (
    <PageContainer>
      {onBack && <BackLink onClick={onBack}>Back to receipts</BackLink>}

      <PageHeader
        eyebrow="Receipt"
        title={receipt.merchantName ? prettyMerchant(receipt.merchantName) : receipt.fileName || "Untitled receipt"}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            {receipt.total != null && (
              <span className="font-medium text-foreground">
                {formatCurrency(receipt.total, currency)}
              </span>
            )}
            <span>{formatDate(receipt.receiptDate ?? receipt.createdAt)}</span>
            <ExtractionBadge status={receipt.extractionStatus} />
            <MatchBadge status={receipt.matchStatus} />
          </span>
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={openFile}>
              <ExternalLink className="size-4" /> View file
            </Button>
            {!isDemo && (unreadable || !stillReading) && (
              <Button variant="outline" size="sm" onClick={retryExtraction} disabled={retrying}>
                {retrying ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />}
                Read again
              </Button>
            )}
          </>
        }
      />

      {unreadable && (
        <div className="mb-5 rounded-lg border border-destructive/30 bg-destructive/10 p-4">
          <p className="flex items-center gap-2 text-sm font-medium text-destructive">
            <AlertTriangle className="size-4" /> We couldn't read this receipt
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {receipt.extractionError ??
              "The photo may be too dark, blurred or cropped. Re-uploading a clearer picture usually works."}{" "}
            You can still match it to a card transaction by hand.
          </p>
        </div>
      )}

      {stillReading && (
        <div className="mb-5 flex items-center gap-2 rounded-lg border bg-muted/40 p-4 text-sm text-muted-foreground">
          <Spinner className="size-4" /> Reading this receipt now. The fields below will fill in shortly.
        </div>
      )}

      {missingVatNumber && (
        <div className="mb-5 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
          <p className="text-sm font-medium text-amber-700">No VAT number on this receipt</p>
          <p className="mt-1 text-sm text-muted-foreground">
            A UK VAT reclaim needs the supplier's VAT registration number on the document, even for a
            simplified receipt. Ask the merchant for a proper VAT invoice and re-upload it — until then
            the VAT on this spend can't be reclaimed.
          </p>
        </div>
      )}

      <div className="grid gap-4 @4xl:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
        <ReceiptPreview
          receiptId={receiptId}
          contentType={receipt.contentType}
          className="h-[420px] @4xl:sticky @4xl:top-4 @4xl:h-[640px]"
        />

        <div className="flex min-w-0 flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">What we read</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid gap-x-8 gap-y-3 @lg:grid-cols-2">
                <Field label="Merchant" value={receipt.merchantName} />
                <Field
                  label="VAT number"
                  value={
                    receipt.merchantVatNumber ? (
                      <span className="font-mono text-xs">{receipt.merchantVatNumber}</span>
                    ) : (
                      <span className="text-amber-600">Missing</span>
                    )
                  }
                />
                <Field label="Address" value={receipt.merchantAddress} />
                <Field label="Receipt number" value={receipt.receiptNumber} />
                <Field label="Date" value={receipt.receiptDate ? formatDate(receipt.receiptDate) : null} />
                {/* A tip added after the card was authorised is the usual reason a receipt total exceeds
                    the transaction matched to it, so it earns a row of its own. */}
                <Field
                  label="Tip"
                  value={receipt.tipAmount != null ? formatCurrency(receipt.tipAmount, currency) : null}
                />
              </dl>
              <div className="mt-4 grid grid-cols-2 gap-3 border-t pt-4">
                <Figure label="VAT" value={receipt.taxTotal != null ? formatCurrency(receipt.taxTotal, currency) : null} />
                <Figure
                  label="Total"
                  strong
                  value={receipt.total != null ? formatCurrency(receipt.total, currency) : null}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Card transaction</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {receipt.matchStatus === "Matched" && receipt.expenseId ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-muted-foreground">
                    Paired with a card transaction
                    {receipt.matchedAt ? ` on ${formatDate(receipt.matchedAt)}` : ""}.
                  </p>
                  {onExpenseClick && (
                    <Button variant="outline" size="sm" onClick={() => onExpenseClick(receipt.expenseId!)}>
                      <Link2 className="size-4" /> Open the expense
                    </Button>
                  )}
                </div>
              ) : stillReading ? (
                <p className="text-muted-foreground">
                  Matching starts once the merchant, total and date have been read.
                </p>
              ) : candidates === null ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Spinner className="size-4" /> Looking for card spend this could belong to...
                </div>
              ) : candidates.length === 0 ? (
                <div className="flex items-start gap-2 text-muted-foreground">
                  <SearchX className="mt-0.5 size-4 shrink-0" />
                  <p>
                    No card spend looks like a match yet. If the purchase has not reached the card feed,
                    this receipt pairs itself when it arrives.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-muted-foreground">
                    Not paired automatically. These card transactions are the closest; pick the right one.
                  </p>
                  <ul className="divide-y rounded-lg border">
                    {/* The server ranks down to faint matches; the top few are realistic, the rest one click away. */}
                    {(showAllCandidates ? candidates : candidates.slice(0, CANDIDATES_SHOWN)).map((candidate) => (
                      <li key={candidate.expenseId} className="flex items-center gap-3 p-3">
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{prettyMerchant(candidate.merchantName)}</div>
                          <div className="text-xs text-muted-foreground">
                            <span className="tabular-nums">
                              {formatCurrency(candidate.amount ?? undefined, candidate.currency ?? currency)}
                            </span>{" "}
                            · {formatDate(candidate.date)}
                            {!candidate.withinAmountTolerance ? (
                              <span className="text-amber-600"> · amount differs</span>
                            ) : null}
                          </div>
                        </div>
                        <ScoreMeter score={Number(candidate.score ?? 0)} />
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={matching !== null}
                          onClick={() => matchTo(candidate.expenseId!)}
                        >
                          {matching === candidate.expenseId ? <Spinner className="size-4" /> : null}
                          Match
                        </Button>
                      </li>
                    ))}
                  </ul>
                  {candidates.length > CANDIDATES_SHOWN ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-muted-foreground"
                      onClick={() => setShowAllCandidates((v) => !v)}
                    >
                      {showAllCandidates
                        ? "Show fewer"
                        : `Show ${candidates.length - CANDIDATES_SHOWN} weaker match${candidates.length - CANDIDATES_SHOWN === 1 ? "" : "es"}`}
                    </Button>
                  ) : null}
                </>
              )}

              <dl className="grid gap-2 border-t pt-3 text-xs text-muted-foreground">
                <MetaRow label="Uploaded" value={formatDate(receipt.createdAt)} />
                <MetaRow label="Source" value={receiptSourceLabel(receipt.source)} />
                <MetaRow label="File" value={receipt.fileName} />
              </dl>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="mt-6">
        <h2 className="mb-2 text-[10px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
          Lines
        </h2>
        {receipt.lines && receipt.lines.length > 0 ? (
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Description</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Qty</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Unit</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Tax rate</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Tax</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {receipt.lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell className="font-medium">{line.description}</TableCell>
                    <TableCell className="text-right tabular-nums">{line.quantity ?? "-"}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {line.unitAmount != null ? formatCurrency(line.unitAmount, currency) : "-"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {line.taxRate != null ? `${line.taxRate}%` : "-"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {line.taxAmount != null ? formatCurrency(line.taxAmount, currency) : "-"}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {line.amount != null ? formatCurrency(line.amount, currency) : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableShell>
        ) : (
          <p className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
            {stillReading
              ? "Still reading the receipt."
              : "No lines were read off this receipt. The header total is still usable for matching and coding."}
          </p>
        )}
      </div>
    </PageContainer>
  );
}

function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">{label}</dt>
      <dd className="mt-0.5 text-sm">
        {value ?? <span className="text-muted-foreground">Not on the receipt</span>}
      </dd>
    </div>
  );
}

function Figure({ label, value, strong }: { label: string; value?: string | null; strong?: boolean }) {
  return (
    <div>
      <div className="text-[10px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">{label}</div>
      <div className={cn("mt-0.5 tabular-nums", strong ? "text-xl font-bold" : "text-lg font-semibold")}>
        {value ?? <span className="text-sm font-normal text-muted-foreground">-</span>}
      </div>
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt>{label}</dt>
      <dd className="truncate text-foreground">{value || "-"}</dd>
    </div>
  );
}

function ExtractionBadge({ status }: { status?: ReceiptResponse["extractionStatus"] }) {
  if (status === "Failed") {
    return (
      <Badge variant="outline" className="border-destructive/30 bg-destructive/15 text-destructive">
        <AlertTriangle className="size-3" /> Unreadable
      </Badge>
    );
  }
  if (status === "Pending") return <Badge variant="secondary">Reading</Badge>;
  return <Badge variant="secondary">Read</Badge>;
}

function MatchBadge({ status }: { status?: ReceiptResponse["matchStatus"] }) {
  if (status === "Matched") {
    return (
      <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/15 text-emerald-600">
        Matched
      </Badge>
    );
  }
  return <Badge variant="secondary">Waiting to match</Badge>;
}
